// The maker over the HTTP API (src/core/make.ts, docs/make.md). Every call
// names the room's language, so what the server says comes back in it.

import { Refused } from "../core/studio.ts";
import type { CreditsInfo, MakeInfo, Tab5Conversation, Tab5GameInfo, Tab5TurnInfo } from "../core/make.ts";
import { lang } from "../ui/lang.ts";

/** The account has too few credits for what it asked: the price, and what it has. */
export class OutOfCredits extends Error {
  constructor(readonly price: number, readonly credits: CreditsInfo) {
    super("not enough credits");
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { "x-pocket-lang": lang() };
  if (body !== undefined) headers["content-type"] = "application/json";
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw await failure(response);
  return (await response.json()) as T;
}

async function failure(response: Response): Promise<Error> {
  const detail = (await response.json().catch(() => null)) as { error?: string; code?: string; price?: number; credits?: CreditsInfo } | null;
  if (response.status === 402 && detail?.code === "CREDITS" && detail.credits) return new OutOfCredits(detail.price ?? 0, detail.credits);
  if (detail?.error && response.status >= 400 && response.status < 600) return new Refused(detail.error);
  return new Error(`the maker answered ${response.status}`);
}

/** Whether the maker is open, and the visitor's credits. Null when this server has no maker. */
export async function makeInfo(): Promise<MakeInfo | null> {
  const response = await fetch("/api/make", { credentials: "same-origin", headers: { "x-pocket-lang": lang() } }).catch(() => null);
  if (!response?.ok || !response.headers.get("content-type")?.includes("json")) return null;
  return (await response.json()) as MakeInfo;
}

// An app for the Tab5 (docs/make.md#the-tab5): an agent makes it, one message at a time.

const tab5Path = (id: string, rest = "") => `/api/make/tab5/${encodeURIComponent(id)}${rest}`;

export const tab5Games = (): Promise<{ games: Tab5GameInfo[] }> => call("GET", "/api/make/tab5");

export const newTab5Game = (text: string): Promise<Tab5TurnInfo> => call("POST", "/api/make/tab5", { text });

export const getTab5Game = (id: string): Promise<Tab5GameInfo> => call("GET", tab5Path(id));

export const tab5Message = (id: string, text: string): Promise<Tab5TurnInfo> => call("POST", tab5Path(id, "/messages"), { text });

export const tab5Conversation = (id: string): Promise<Tab5Conversation> => call("GET", tab5Path(id, "/conversation"));

export const stopTab5Turn = (id: string): Promise<{ ok: true }> => call("POST", tab5Path(id, "/abort"), {});

